import React, { useEffect, useState } from 'react';
import { useApiClient } from '../services/api';
import { Users, Crown, GripVertical } from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import CustomLoader from './CustomLoader';

interface TeamManagementProps {
  sponsorId: string;
}

interface Member {
  id: string;
  name: string;
  email: string;
  linkedin: string;
  skills: string[];
  isLeader: boolean;
}

interface Team {
  id: string;
  teamName: string;
  hackdayId: string;
  hackdayName: string;
  members: Member[];
}

interface SoloParticipant {
  id: string;
  teamAssignmentId: string;
  name: string;
  email: string;
  linkedin: string;
  skills: string[];
  hackdayId: string;
  hackdayName: string;
}

interface LookingForTeamParticipant {
  id: string;
  teamAssignmentId: string;
  name: string;
  email: string;
  linkedin: string;
  skills: string[];
  lookingForSkills?: string[];
  hackdayId: string;
  hackdayName: string;
}

interface Hackday {
  id: string;
  title: string;
}

interface DraggableMemberProps {
  member: Member | SoloParticipant | LookingForTeamParticipant;
  teamId: string | null;
}

const DraggableMember: React.FC<DraggableMemberProps> = ({ member, teamId }) => {
  const isLeader = 'isLeader' in member && member.isLeader;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${teamId}-${member.id}`,
    data: { member, teamId },
    disabled: isLeader, // Disable dragging for team leaders
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`
        bg-white rounded-lg p-3 shadow-sm border-2
        ${isLeader ? 'border-yellow-400 cursor-not-allowed' : 'border-gray-200 hover:border-purple-400 cursor-grab active:cursor-grabbing'}
        transition-all duration-200
      `}
    >
      <div className="flex items-start space-x-2">
        {!isLeader && <GripVertical className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />}
        {isLeader && <Crown className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{member.name}</p>
          {isLeader && <p className="text-xs text-yellow-600 mt-0.5">Team Leader</p>}
          {member.skills && member.skills.length > 0 && (
            <p className="text-xs text-gray-500 mt-1 truncate">
              {member.skills.join(', ')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

interface DroppableTeamProps {
  team: Team;
  children: React.ReactNode;
}

const DroppableTeam: React.FC<DroppableTeamProps> = ({ team, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `team-${team.id}`,
    data: { team },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        bg-white bg-opacity-40 backdrop-blur-sm rounded-xl p-4 shadow-lg border-2
        ${isOver ? 'border-purple-500 bg-purple-50' : 'border-white border-opacity-50'}
        transition-all duration-200 min-h-[200px]
      `}
    >
      <div className="flex items-center space-x-2 mb-4">
        <Users className="h-5 w-5 text-purple-600" />
        <h3 className="font-bold text-gray-800 text-lg">{team.teamName}</h3>
        <span className="text-xs text-gray-500">({team.members.length})</span>
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

interface DroppableSoloProps {
  children: React.ReactNode;
}

const DroppableSolo: React.FC<DroppableSoloProps> = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'solo',
    data: { type: 'solo' },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        bg-white bg-opacity-40 backdrop-blur-sm rounded-xl p-4 shadow-lg border-2
        ${isOver ? 'border-gray-500 bg-gray-50' : 'border-white border-opacity-50'}
        transition-all duration-200 min-h-[200px]
      `}
    >
      <div className="flex items-center space-x-2 mb-4">
        <Users className="h-5 w-5 text-gray-600" />
        <h3 className="font-bold text-gray-800 text-lg">Solo</h3>
        <span className="text-xs text-gray-500">(Not looking)</span>
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

interface DroppableLookingProps {
  children: React.ReactNode;
}

const DroppableLooking: React.FC<DroppableLookingProps> = ({ children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: 'looking',
    data: { type: 'looking' },
  });

  return (
    <div
      ref={setNodeRef}
      className={`
        bg-white bg-opacity-40 backdrop-blur-sm rounded-xl p-4 shadow-lg border-2
        ${isOver ? 'border-blue-500 bg-blue-50' : 'border-white border-opacity-50'}
        transition-all duration-200 min-h-[200px]
      `}
    >
      <div className="flex items-center space-x-2 mb-4">
        <Users className="h-5 w-5 text-blue-600" />
        <h3 className="font-bold text-gray-800 text-lg">Looking for Team</h3>
      </div>
      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
};

export const TeamManagement: React.FC<TeamManagementProps> = ({ sponsorId }) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [lookingForTeam, setLookingForTeam] = useState<LookingForTeamParticipant[]>([]);
  const [soloParticipants, setSoloParticipants] = useState<SoloParticipant[]>([]);
  const [hackdays, setHackdays] = useState<Hackday[]>([]);
  const [selectedHackday, setSelectedHackday] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeMember, setActiveMember] = useState<Member | SoloParticipant | LookingForTeamParticipant | null>(null);
  const apiClient = useApiClient();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const hackdayId = selectedHackday || undefined;
        const response = await apiClient.getTeamsWithMembers(sponsorId, hackdayId);

        if (response.success && response.data) {
          setTeams(response.data.teams);
          setLookingForTeam(response.data.lookingForTeam || []);
          setSoloParticipants(response.data.soloParticipants);
          setHackdays(response.data.hackdays);

          // Set first hackday as selected if not already set and hackdays exist
          if (!selectedHackday && response.data.hackdays.length > 0) {
            setSelectedHackday(response.data.hackdays[0].id);
          }
        }
      } catch (error) {
        console.error('Error fetching team management data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sponsorId, selectedHackday, apiClient]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const { member } = active.data.current as { member: Member | SoloParticipant | LookingForTeamParticipant; teamId: string | null };
    setActiveMember(member);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveMember(null);

    if (!over) return;

    const { member, teamId: fromTeamId } = active.data.current as {
      member: Member | SoloParticipant | LookingForTeamParticipant;
      teamId: string | null;
    };

    const overId = over.id as string;

    // Determine source type
    let fromType: 'team' | 'solo' | 'looking';
    if (fromTeamId) {
      fromType = 'team';
    } else {
      fromType = lookingForTeam.some(p => p.id === member.id) ? 'looking' : 'solo';
    }

    // Determine destination type
    let toType: 'team' | 'solo' | 'looking';
    if (overId.startsWith('team-')) {
      toType = 'team';
    } else if (overId === 'looking') {
      toType = 'looking';
    } else {
      toType = 'solo';
    }

    // If no change, return
    if (fromType === toType && toType !== 'team') return;
    if (toType === 'team' && fromTeamId === overId.replace('team-', '')) return;

    // Save current state for reverting
    const prevTeams = [...teams];
    const prevLooking = [...lookingForTeam];
    const prevSolo = [...soloParticipants];

    const teamAssignmentId = 'teamAssignmentId' in member ? member.teamAssignmentId : '';

    // Handle different drag and drop scenarios
    try {
      if (toType === 'solo') {
        // Moving TO Solo
        if (fromType === 'team') {
          // From team to solo
          if (!fromTeamId) return; // Type guard
          const fromTeam = teams.find(t => t.id === fromTeamId);
          if (!fromTeam) return;

          // Optimistic update
          setTeams(teams.map(t => t.id === fromTeamId ? { ...t, members: t.members.filter(m => m.id !== member.id) } : t));
          const newSolo: SoloParticipant = {
            id: member.id,
            teamAssignmentId: '',
            name: member.name,
            email: member.email,
            linkedin: member.linkedin,
            skills: member.skills,
            hackdayId: fromTeam.hackdayId,
            hackdayName: fromTeam.hackdayName,
          };
          setSoloParticipants([...soloParticipants, newSolo]);

          apiClient.invalidateCache('teammanagement');
          const response = await apiClient.moveParticipantToSolo({
            participantId: member.id,
            fromTeamId,
            hackdayId: fromTeam.hackdayId,
          });

          if (!response.success) {
            alert(response.error || 'Failed to move to solo');
            setTeams(prevTeams);
            setSoloParticipants(prevSolo);
          }
        } else if (fromType === 'looking') {
          // From looking to solo - just update status
          setLookingForTeam(lookingForTeam.filter(p => p.id !== member.id));
          setSoloParticipants([...soloParticipants, { ...member as LookingForTeamParticipant }]);

          apiClient.invalidateCache('teammanagement');
          const response = await apiClient.updateStatusToSolo({ participantId: member.id, teamAssignmentId });

          if (!response.success) {
            alert(response.error || 'Failed to update status');
            setLookingForTeam(prevLooking);
            setSoloParticipants(prevSolo);
          }
        }
      } else if (toType === 'looking') {
        // Moving TO Looking for Team
        if (fromType === 'solo') {
          // From solo to looking - just update status
          setSoloParticipants(soloParticipants.filter(p => p.id !== member.id));
          setLookingForTeam([...lookingForTeam, { ...member as SoloParticipant, lookingForSkills: [] }]);

          apiClient.invalidateCache('teammanagement');
          const response = await apiClient.updateStatusToLooking({ participantId: member.id, teamAssignmentId });

          if (!response.success) {
            alert(response.error || 'Failed to update status');
            setSoloParticipants(prevSolo);
            setLookingForTeam(prevLooking);
          }
        } else if (fromType === 'team') {
          alert('Cannot move team members directly to "Looking for Team". Move to Solo first.');
        }
      } else {
        // Moving TO a Team
        const toTeamId = overId.replace('team-', '');
        const toTeam = teams.find(t => t.id === toTeamId);
        if (!toTeam) return;

        let hackdayId: string;
        if (fromType === 'team') {
          if (!fromTeamId) return; // Type guard
          const fromTeam = teams.find(t => t.id === fromTeamId);
          if (!fromTeam) return;
          hackdayId = fromTeam.hackdayId;
        } else {
          hackdayId = (member as SoloParticipant | LookingForTeamParticipant).hackdayId;
        }

        if (hackdayId !== toTeam.hackdayId) {
          alert('Cannot move participants between different events');
          return;
        }

        // Optimistic update
        let updatedTeams = [...teams];
        if (fromType === 'team') {
          if (!fromTeamId) return; // Type guard
          updatedTeams = updatedTeams.map(t => t.id === fromTeamId ? { ...t, members: t.members.filter(m => m.id !== member.id) } : t);
        } else if (fromType === 'solo') {
          setSoloParticipants(soloParticipants.filter(p => p.id !== member.id));
        } else {
          setLookingForTeam(lookingForTeam.filter(p => p.id !== member.id));
        }

        const newMember: Member = {
          id: member.id,
          name: member.name,
          email: member.email,
          linkedin: member.linkedin,
          skills: member.skills,
          isLeader: false,
        };
        updatedTeams = updatedTeams.map(t => t.id === toTeamId ? { ...t, members: [...t.members, newMember] } : t);
        setTeams(updatedTeams);

        apiClient.invalidateCache('teammanagement');
        const response = await apiClient.moveParticipantToTeam({
          participantId: member.id,
          fromTeamId,
          toTeamId,
          hackdayId,
        });

        if (!response.success) {
          alert(response.error || 'Failed to move to team');
          setTeams(prevTeams);
          setSoloParticipants(prevSolo);
          setLookingForTeam(prevLooking);
        }
      }
    } catch (error) {
      console.error('Error in drag and drop:', error);
      alert('An error occurred');
      setTeams(prevTeams);
      setSoloParticipants(prevSolo);
      setLookingForTeam(prevLooking);
    }
  };

  if (loading) {
    return (
      <CustomLoader />
    );
  }

  return (
    <div>
      {/* Centered Header */}
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold my-4 font-space"
          style={{
            background: 'linear-gradient(135deg, #500295, #F87789)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
          Team Management
        </h2>
        <p className="text-sm text-gray-600">Drag and drop participants to organize teams</p>
      </div>

      {/* Hackday Filter */}
      <div className="flex justify-center mb-6">
        <select
          value={selectedHackday}
          onChange={(e) => setSelectedHackday(e.target.value)}
          className="px-4 py-2 rounded-lg bg-white bg-opacity-60 backdrop-blur-sm border border-white border-opacity-30 text-gray-800 focus:outline-none focus:ring-2 focus:ring-purple-500"
          disabled={hackdays.length === 0}
        >
          {hackdays.length === 0 ? (
            <option value="">No Hackdays</option>
          ) : (
            hackdays.map((hackday) => (
              <option key={hackday.id} value={hackday.id}>
                {hackday.title}
              </option>
            ))
          )}
        </select>
      </div>

      {teams.length === 0 && soloParticipants.length === 0 && lookingForTeam.length === 0 ? (
        <div className="bg-white bg-opacity-20 backdrop-blur-md rounded-xl p-12 shadow-lg border border-white border-opacity-30 text-center">
          <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Participants</h3>
          <p className="text-gray-600 mb-4">No participants found for the selected hackday.</p>
          <p className="text-sm text-gray-500">
            Participants will appear here once they register for your events.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {/* Solo Participants */}
            <DroppableSolo>
              {soloParticipants.length > 0 ? (
                soloParticipants.map((solo) => (
                  <DraggableMember key={solo.id} member={solo} teamId={null} />
                ))
              ) : (
                <p className="text-sm text-gray-500 italic p-2">No solo participants</p>
              )}
            </DroppableSolo>

            {/* Looking for Team */}
            <DroppableLooking>
              {lookingForTeam.length > 0 ? (
                lookingForTeam.map((participant) => (
                  <DraggableMember key={participant.id} member={participant} teamId={null} />
                ))
              ) : (
                <p className="text-sm text-gray-500 italic p-2">No one looking for team</p>
              )}
            </DroppableLooking>

            {/* Teams */}
            {teams.map((team) => (
              <DroppableTeam key={team.id} team={team}>
                {team.members.map((member) => (
                  <DraggableMember key={member.id} member={member} teamId={team.id} />
                ))}
              </DroppableTeam>
            ))}
          </div>

          <DragOverlay>
            {activeMember ? (
              <div className="bg-white rounded-lg p-3 shadow-xl border-2 border-purple-400 opacity-90">
                <div className="flex items-start space-x-2">
                  <GripVertical className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{activeMember.name}</p>
                    {'isLeader' in activeMember && activeMember.isLeader && (
                      <p className="text-xs text-yellow-600 mt-0.5">Team Leader</p>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default TeamManagement;
